import { GRAMMAR, PLAGIARISM } from '@src/components/Cards/constants'
import { showCheggAuthModal } from '@src/state/auth/actions'
import { getLoggedIn, getPaidMember } from '@src/state/auth/selectors'
import {
  checkResults,
  checkStartFail,
  CheckStartFail,
  PARTIAL_PLAGIARISM_CHECK_END,
  partialPlagiarismCheckEnd,
  runChecks,
  RunChecksPayload,
} from '@src/state/checks/actions'
import { EXPERT_CHECK } from '@src/state/document/types'
import {
  AUTH_MODAL_TITLE,
  CHECK_FAILED_MODAL,
  clearAllModals,
  hideModal,
  HideModalPayload,
  ModalNames,
  ModalPayload,
  PAPER_UPLOADED_NON_PLUS_MODAL,
  PAYMENT_MODAL,
  REVIEWING_PAPER_MODAL,
  showModal,
} from '@src/state/modals/actions'
import { FreeTrialPayload, handleFreeTrialStart } from '@src/state/payment/actions'
import { RootState } from '@src/state/types'
import { paymentFlowEnd, paymentFlowStart } from '@src/state/workspace/actions'
import {
  selectCurrentCheckCategory,
  selectInitialCheckCategory,
} from '@src/state/workspace/selectors'
import { Check } from '@src/types/checks'
import { PLANS_PAGE } from '@src/utils/dataTestIds'
import {
  LOCATION_CHANGE,
  push,
  RouterAction,
  RouterActionType,
} from 'connected-react-router'
import { createLogic } from 'redux-logic'
import { getType } from 'typesafe-actions'
import { allChecksComplete, allChecksFailed } from '../checks/selectors'
import { isDemoSelector } from '../router/selectors'

export const runChecksModalLogic = createLogic<RootState, RunChecksPayload>({
  type: getType(runChecks),
  process({ getState, action }, dispatch, done) {
    const { isRecheck, checkCategory } = action.payload
    const state = getState()
    const initialCheckCategory = selectInitialCheckCategory(state)
    if (
      checkCategory === GRAMMAR &&
      !isRecheck &&
      initialCheckCategory !== EXPERT_CHECK
    ) {
      dispatch(
        showModal({ name: REVIEWING_PAPER_MODAL, modalProps: { isRecheck: false } })
      )
    }
    done()
  },
})

export const checkStartFailureLogic = createLogic<RootState, CheckStartFail>({
  type: getType(checkStartFail),
  process({ action }, dispatch, done) {
    const { grammar } = action.payload
    if (grammar) {
      dispatch(showModal({ name: CHECK_FAILED_MODAL }))
    }
    done()
  },
})

export const handleCheckCompleteModalLogic = createLogic<RootState, Check>({
  type: [
    getType(checkResults.success),
    getType(checkResults.failure),
    getType(partialPlagiarismCheckEnd),
  ],
  validate({ getState, action }, allow, reject) {
    const state = getState()
    // in case race condition where partialPlagiarismCheck ends BEFORE all grammar checks come back
    if (
      action.type === PARTIAL_PLAGIARISM_CHECK_END &&
      allChecksComplete(state.checks, state.workspace.checksToExpect)
    ) {
      return allow(action)
    }

    const check = action.payload
    const { type } = check

    const mergedCheckState = {
      ...state.checks,
      [type]: {
        status: check.status,
        finished: true,
        count: check.count,
        type,
      },
    }

    if (
      type !== PLAGIARISM &&
      allChecksComplete(mergedCheckState, state.workspace.checksToExpect) &&
      !state.workspace.partialPlagiarismRunning // prevents modal from hiding
    ) {
      allow(action)
    } else {
      reject(action)
    }
  },
  process({ getState, action }, dispatch, done) {
    dispatch(hideModal({ name: REVIEWING_PAPER_MODAL }, {}))
    const state = getState()
    const check = action.payload
    const { checks } = state
    let mergedCheckState = { checks }
    const currentCheckCategory = selectCurrentCheckCategory(state)
    const isDemo = isDemoSelector(state)

    // plagiarismPartialCheck doesn't have check payload
    // we want to only merge the check payload in when it's a grammar
    if (action.type !== PARTIAL_PLAGIARISM_CHECK_END) {
      const { type } = check
      mergedCheckState = {
        checks,
        [type]: {
          status: check.status,
          finished: true,
          count: check.count,
          type,
        },
      }
    }

    if (allChecksFailed(mergedCheckState)) {
      dispatch(showModal({ name: CHECK_FAILED_MODAL }))
    } else if (!isDemo && !getPaidMember(state) && currentCheckCategory === GRAMMAR) {
      dispatch(showModal({ name: PAPER_UPLOADED_NON_PLUS_MODAL }))
    }
    done()
  },
})

export const freeTrialStartLogic = createLogic<RootState, FreeTrialPayload>({
  type: getType(handleFreeTrialStart),
  process({ getState, action }, dispatch, done) {
    const { origin } = action.payload as FreeTrialPayload
    const state = getState()
    const { router } = state
    const { cspUpsell } = state.featureFlags

    if (origin === PAPER_UPLOADED_NON_PLUS_MODAL) {
      dispatch(hideModal({ name: PAPER_UPLOADED_NON_PLUS_MODAL }, {}))
    }

    if (getLoggedIn(state)) {
      if (cspUpsell?.cspUpsellShowInterstitial && origin !== PLANS_PAGE) {
        dispatch(push('/plans', { redirectPath: router.location.pathname }))
      } else {
        dispatch(
          showModal({
            name: PAYMENT_MODAL,
          })
        )
      }
    } else {
      dispatch(showCheggAuthModal({ experience: 'signup', title: AUTH_MODAL_TITLE }))
    }
    done()
  },
})

export const dismissOnPopStateLogic = createLogic<RootState, RouterAction>({
  type: LOCATION_CHANGE,
  validate({ action }, allow, reject) {
    const { payload } = action as any
    const routerAction = payload.action as RouterActionType
    if (routerAction === 'POP') {
      allow(action)
    } else {
      reject(action)
    }
  },
  process(_, dispatch, done) {
    dispatch(clearAllModals())
    if (window.globalCheggAuth && window.globalCheggAuth.trigger) {
      window.globalCheggAuth.trigger('hide')
    }
    done()
  },
})

export const hideModalLogic = createLogic<RootState, HideModalPayload>({
  type: getType(hideModal),
  validate({ action, getState }, allow, reject) {
    const { name } = action.payload
    const { modals } = getState()
    if (modals[name]) {
      return allow(action)
    }
    return reject({ type: `REJECTED ${getType(hideModal)}` })
  },
})

export const authFlowLogic = createLogic<RootState, ModalPayload<ModalNames>>({
  type: [getType(showModal), getType(hideModal)],
  validate({ action }, allow, reject) {
    if (action.payload.name === PAYMENT_MODAL) {
      return allow(action)
    }
    reject(action)
  },
  process({ action }, dispatch, done) {
    if (action.type === getType(showModal)) {
      dispatch(paymentFlowStart())
    }
    if (action.type === getType(hideModal)) {
      dispatch(paymentFlowEnd())
    }
    done()
  },
})

export const modalLogic = [
  authFlowLogic,
  hideModalLogic,
  runChecksModalLogic,
  handleCheckCompleteModalLogic,
  freeTrialStartLogic,
  dismissOnPopStateLogic,
  checkStartFailureLogic,
]



// WEBPACK FOOTER //
// ./src/state/modals/logic.ts